import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { orangeBookAPI } from '../services/api';

function OrangeBookDetail() {
  const { applicationNumber } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProductDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await orangeBookAPI.search(applicationNumber, 'both', 100);
        if (response.data && response.data.results && response.data.results.length > 0) {
          // Find the product with matching application number
          const matchingProduct = response.data.results.find(
            p => p.application_number === applicationNumber
          );
          if (matchingProduct) {
            setProduct(matchingProduct);
          } else {
            setProduct(response.data.results[0]);
          }
        } else {
          setError('Product not found');
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    if (applicationNumber) {
      fetchProductDetails();
    }
  }, [applicationNumber]);

  const getFdaApplNo = (appNum) => {
    if (!appNum) return '';
    // Remove ANDA/NDA/BLA prefix to get just the numeric part
    return appNum.replace(/^[A-Z]+/, '');
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div className="loading-spinner"></div>
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-message">
          <p>⚠️ Error: {error}</p>
          <button onClick={() => navigate('/orangebook')} className="btn-primary">
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="page-container">
        <div className="error-message">
          <p>Product not found</p>
          <button onClick={() => navigate('/orangebook')} className="btn-primary">
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <button 
        onClick={() => navigate(-1)} 
        className="back-button"
        style={{
          marginBottom: '1rem',
          padding: '0.5rem 1rem',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#e9ecef';
          e.target.style.borderColor = '#adb5bd';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#f8f9fa';
          e.target.style.borderColor = '#dee2e6';
        }}
      >
        <span>←</span> Back to Search
      </button>

      <div style={{
        background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
        color: 'white',
        padding: '2rem',
        borderRadius: '8px',
        marginBottom: '2rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 'bold' }}>
          {product.brand_name || product.openfda?.brand_name?.[0] || 'Unknown Product'}
        </h1>
        <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.1rem', opacity: 0.95 }}>
          {product.generic_name || product.openfda?.generic_name?.[0] || 'Generic name not available'}
        </p>
      </div>

      <div className="detail-section">
        <h2 className="section-title">Product Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Application Number:</span>
            <span className="info-value">{product.application_number || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Product Type:</span>
            <span className="info-value">{product.product_type || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Manufacturer:</span>
            <span className="info-value">{product.labeler_name || product.openfda?.manufacturer_name?.[0] || 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Marketing Status:</span>
            <span className="info-value">{product.marketing_status || 'N/A'}</span>
          </div>
        </div>
      </div>

      {(product.route || product.dosage_form || product.strength) && (
        <div className="detail-section">
          <h2 className="section-title">Formulation Details</h2>
          <div className="info-grid">
            {product.dosage_form && (
              <div className="info-item">
                <span className="info-label">Dosage Form:</span>
                <span className="info-value">{product.dosage_form}</span>
              </div>
            )}
            {product.route && (
              <div className="info-item">
                <span className="info-label">Route:</span>
                <span className="info-value">
                  {Array.isArray(product.route) ? product.route.join(', ') : product.route}
                </span>
              </div>
            )}
            {product.strength && (
              <div className="info-item">
                <span className="info-label">Strength:</span>
                <span className="info-value">{product.strength}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {product.te_code && (
        <div className="detail-section">
          <h2 className="section-title">Therapeutic Equivalence</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">TE Code:</span>
              <span style={{ 
                padding: '0.5rem 1rem',
                backgroundColor: product.te_code.startsWith('A') ? '#d4edda' : '#f8d7da',
                color: product.te_code.startsWith('A') ? '#155724' : '#721c24',
                borderRadius: '4px',
                fontWeight: 'bold',
                fontSize: '1.1rem'
              }}>
                {product.te_code}
              </span>
            </div>
            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
              <span className="info-label">Meaning:</span>
              <span className="info-value">
                {product.te_code.startsWith('A') 
                  ? 'Products coded A are considered therapeutically equivalent to other pharmaceutically equivalent products.' 
                  : 'Products coded B may not be considered therapeutically equivalent.'}
              </span>
            </div>
            {product.reference_listed_drug && (
              <div className="info-item">
                <span className="info-label">Reference Listed Drug:</span>
                <span className="info-value">
                  {product.reference_listed_drug === 'Yes' ? '✓ Yes' : 'No'}
                </span>
              </div>
            )}
            {product.reference_standard && (
              <div className="info-item">
                <span className="info-label">Reference Standard:</span>
                <span className="info-value">
                  {product.reference_standard === 'Yes' ? '✓ Yes' : 'No'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {product.active_ingredients && product.active_ingredients.length > 0 && (
        <div className="detail-section">
          <h2 className="section-title">Active Ingredients</h2>
          <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
            <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
              {product.active_ingredients.map((ing, i) => (
                <li key={i} style={{ padding: '0.5rem 0' }}>
                  <strong>{ing.name}</strong> - {ing.strength}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {product.openfda?.substance_name && product.openfda.substance_name.length > 0 && (
        <div className="detail-section">
          <h2 className="section-title">Substance Names</h2>
          <div className="info-grid">
            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
              <span className="info-value">
                {product.openfda.substance_name.join(', ')}
              </span>
            </div>
          </div>
        </div>
      )}

      {product.openfda?.pharm_class_epc && product.openfda.pharm_class_epc.length > 0 && (
        <div className="detail-section">
          <h2 className="section-title">Pharmacologic Class</h2>
          <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
            {product.openfda.pharm_class_epc.map((pc, i) => (
              <div key={i} style={{ 
                display: 'inline-block',
                padding: '0.5rem 1rem',
                backgroundColor: '#e9ecef',
                borderRadius: '20px',
                margin: '0.25rem',
                fontSize: '0.9rem'
              }}>
                {pc}
              </div>
            ))}
          </div>
        </div>
      )}

      {product.openfda?.product_ndc && product.openfda.product_ndc.length > 0 && (
        <div className="detail-section">
          <h2 className="section-title">NDC Codes</h2>
          <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
            <div style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
              {product.openfda.product_ndc.map((ndc, i) => (
                <div key={i} style={{ padding: '0.25rem 0' }}>{ndc}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="detail-section">
        <h2 className="section-title">Source Information</h2>
        <div className="info-grid">
          <div className="info-item" style={{ gridColumn: '1 / -1' }}>
            <span className="info-label">FDA Orange Book:</span>
            <a 
              href={`https://www.accessdata.fda.gov/scripts/cder/daf/index.cfm?event=overview.process&ApplNo=${getFdaApplNo(product.application_number)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#3498db',
                textDecoration: 'none',
                fontWeight: 'bold',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              View on FDA Website
              <span style={{ fontSize: '0.8rem' }}>↗</span>
            </a>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
        <button 
          onClick={() => navigate(-1)} 
          className="btn-secondary"
          style={{
            padding: '0.75rem 2rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
        >
          ← Back to Search Results
        </button>
      </div>
    </div>
  );
}

export default OrangeBookDetail;
